import React from 'react';

interface StarRatingProps {
  rating: number | string | undefined | null;
  maxRating?: number; // Default is 5
  className?: string; // Allow passing custom class names
}

const StarRating: React.FC<StarRatingProps> = ({ rating, maxRating = 5, className = '' }) => {
  // Function to normalize rating and handle non-numeric values
  const getNumericRating = (value: number | string | undefined | null): number => {
    if (typeof value === 'number' && !isNaN(value)) {
      // Clamp the rating between 0 and maxRating
      return Math.max(0, Math.min(value, maxRating));
    }
    return 0; // Treat N/A, undefined, null, or non-numbers as 0 stars for rendering
  };

  const numericRating = getNumericRating(rating);
  const fullStars = Math.floor(numericRating);
  // Basic half-star logic (can be refined)
  const hasHalfStar = numericRating % 1 >= 0.5;
  const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

  // Check if the original rating was non-numeric or invalid
  const isRatingNotAvailable = typeof rating !== 'number' || isNaN(rating);

  return (
    <div className={`star-rating ${className}`.trim()} aria-label={`Rating: ${isRatingNotAvailable ? 'Not available' : `${numericRating.toFixed(1)} out of ${maxRating}`}`}>
      {isRatingNotAvailable ? (
        <span className="rating-na">N/A</span>
      ) : (
        <>
          {[...Array(fullStars)].map((_, i) => (
            <span key={`full-${i}`} className="star full-star" aria-hidden="true">★</span>
          ))}
          {hasHalfStar && (
            // Using a simple text representation for half-star, could use icons/SVG
            <span key="half" className="star half-star" aria-hidden="true">★</span> // Could style differently via CSS
          )}
          {[...Array(emptyStars)].map((_, i) => (
            <span key={`empty-${i}`} className="star empty-star" aria-hidden="true">☆</span>
          ))}
          {/* Optionally display the numeric value alongside stars */}
          {/* <span className="rating-value">({numericRating.toFixed(1)})</span> */}
        </>
      )}
    </div>
  );
};

export default StarRating; 